(() => {
  // Shopify Forms renders in an open shadow root. Scope placement to its
  // minimized "tease" step; never move its open form, success state or overlay.
  if (document.documentElement.dataset.lnpOfferPlacementReady) return;
  document.documentElement.dataset.lnpOfferPlacementReady = 'true';

  const hostSelector = 'shopify-forms-embed';
  const barSelector = '.ln-mobile-bar, [data-lnp-mobile-cart], .lnp-mobile-cart';
  const dialogSelector = '#cart-notification, cart-drawer, header-drawer > details, header details-modal > details';
  const openDialogSelector = '#cart-notification.active, cart-drawer.active, header-drawer > details[open], header details-modal > details[open]';
  const pendingHosts = new WeakSet();
  const observedRoots = new WeakSet();
  const observedBars = new Set();
  const observedDialogs = new Map();
  const formHosts = new Set();
  let frame;
  let lastBottom;
  const css = `
    #app-embed[data-current-step='tease'] {
      position: fixed !important;
      left: 12px !important;
      right: auto !important;
      top: auto !important;
      bottom: max(var(--lnp-offer-bottom, 16px), calc(16px + env(safe-area-inset-bottom))) !important;
      width: max-content !important;
      max-width: calc(100vw - 24px) !important;
      padding: 0 !important;
      transform: none !important;
      rotate: none !important;
    }
    #app-embed[data-current-step='tease'] > div {
      rotate: none !important;
      transform: none !important;
    }
    #app-embed[data-current-step='tease'] [data-testid='form-teaser'] {
      box-sizing: border-box !important;
      width: auto !important;
      min-width: 44px !important;
      max-width: min(220px, calc(100vw - 24px)) !important;
      height: auto !important;
      min-height: 44px !important;
      margin: 0 !important;
      padding: 8px 10px !important;
      gap: 8px !important;
      background: rgba(249, 244, 250, .96);
      border: 1px solid rgba(255, 255, 255, .92);
      border-radius: 16px;
      box-shadow: 0 5px 20px rgba(45, 58, 77, .14);
    }
    #app-embed[data-current-step='tease'] [data-testid='form-teaser'] h3 {
      margin: 0 !important;
      min-width: 0 !important;
      color: #344154 !important;
      font-family: var(--font-body-family, Arial, sans-serif) !important;
      font-size: 12px !important;
      font-weight: 650 !important;
      line-height: 1.35 !important;
      text-align: left !important;
      white-space: normal !important;
      overflow-wrap: anywhere !important;
    }
    #app-embed[data-current-step='tease'] [data-testid='form-teaser']:focus-visible {
      outline: 2px solid #536f89;
      outline-offset: 3px;
    }
    :host([data-ln-cart-dialog-open]) #app-embed[data-current-step='tease'] {
      display: none !important;
      visibility: hidden !important;
      pointer-events: none !important;
    }
  `;

  const updateBottom = () => {
    frame = null;
    let bottom = 16;
    observedBars.forEach((bar) => {
      if (!bar.isConnected) {
        resizeObserver?.unobserve(bar);
        observedBars.delete(bar);
        return;
      }
      const box = bar.getBoundingClientRect();
      if (box.width && box.height && box.top < window.innerHeight && box.bottom > 0) {
        bottom = Math.max(bottom, Math.ceil(window.innerHeight - box.top + 12));
      }
    });
    if (bottom !== lastBottom) {
      document.documentElement.style.setProperty('--lnp-offer-bottom', `${bottom}px`);
      lastBottom = bottom;
    }
  };
  const scheduleUpdate = () => {
    if (frame == null) frame = window.requestAnimationFrame(updateBottom);
  };
  const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(scheduleUpdate) : null;

  // Keep the historical cart-dialog flag for the minimized Forms teaser while
  // any cart, header navigation or header search overlay is open.
  const isDialogOpen = () => Boolean(document.querySelector(openDialogSelector));
  const syncDialogState = () => {
    observedDialogs.forEach((observer, dialog) => {
      if (!dialog.isConnected) {
        observer.disconnect();
        observedDialogs.delete(dialog);
      }
    });
    const open = isDialogOpen();
    formHosts.forEach((host) => {
      if (!host.isConnected) {
        formHosts.delete(host);
        return;
      }
      host.toggleAttribute('data-ln-cart-dialog-open', open);
    });
    scheduleUpdate();
  };

  const attach = (host, attempt = 0) => {
    if (!host.isConnected) return;
    formHosts.add(host);
    host.toggleAttribute(
      'data-ln-cart-dialog-open',
      isDialogOpen()
    );
    const shadow = host.shadowRoot;
    if (!shadow) {
      if (!customElements.get(hostSelector) || pendingHosts.has(host) || attempt >= 60) return;
      pendingHosts.add(host);
      window.setTimeout(() => {
        pendingHosts.delete(host);
        attach(host, attempt + 1);
      }, attempt < 30 ? 100 : 1000);
      return;
    }
    if (!shadow.querySelector('style[data-lnp-offer-style]')) {
      const style = document.createElement('style');
      style.dataset.lnpOfferStyle = 'true';
      style.textContent = css;
      shadow.appendChild(style);
    }
    if (!observedRoots.has(shadow)) {
      observedRoots.add(shadow);
      new MutationObserver(() => attach(host)).observe(shadow, { childList: true });
    }
  };
  const scan = (scope) => {
    const dialogs = [...scope.querySelectorAll(dialogSelector)];
    if (scope.matches?.(dialogSelector)) dialogs.push(scope);
    dialogs.forEach((dialog) => {
      if (observedDialogs.has(dialog)) return;
      const observer = new MutationObserver(syncDialogState);
      observer.observe(dialog, { attributes: true, attributeFilter: ['class', 'open'] });
      observedDialogs.set(dialog, observer);
    });
    if (scope.matches?.(hostSelector)) attach(scope);
    scope.querySelectorAll(hostSelector).forEach((host) => attach(host));
    const bars = [...scope.querySelectorAll(barSelector)];
    if (scope.matches?.(barSelector)) bars.push(scope);
    bars.forEach((bar) => {
      if (observedBars.has(bar)) return;
      observedBars.add(bar);
      resizeObserver?.observe(bar);
      scheduleUpdate();
    });
  };

  scan(document);
  syncDialogState();
  updateBottom();
  customElements.whenDefined(hostSelector).then(() => scan(document));
  new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node.nodeType === 1) scan(node);
    }));
    syncDialogState();
  }).observe(document.body, { childList: true, subtree: true });
  window.addEventListener('resize', scheduleUpdate, { passive: true });
  window.addEventListener('scroll', scheduleUpdate, { passive: true });
  window.visualViewport?.addEventListener('resize', scheduleUpdate, { passive: true });
  window.visualViewport?.addEventListener('scroll', scheduleUpdate, { passive: true });
  document.addEventListener('shopify:section:load', () => { scan(document); syncDialogState(); });
})();
